import {AfterViewInit, Component, ElementRef, output, signal, viewChild} from '@angular/core';
import {FormsModule} from '@angular/forms';
import {BaseInput} from '../base-input/base-input';
import {Tabs} from '../tabs/tabs';
import {Tab} from '../tabs/tab/tab';
import {SelectComponent} from '../select/select.component';
import {Text} from '../text/text';

const FONTS = ['Caveat', 'Dancing Script', 'Pacifico', 'Satisfy'] as const;
type SignatureFont = typeof FONTS[number];

@Component({
  selector: 'app-signature-pad',
  standalone: true,
  imports: [FormsModule, Tabs, Tab, SelectComponent, Text],
  templateUrl: './signature-pad.component.html',
  host: {'(window:resize)': 'onResize()'},
  styleUrl: './signature-pad.component.css',
})
export class SignaturePadComponent extends BaseInput implements AfterViewInit {
  signatureChange = output<string>();

  activeTab = signal<'draw' | 'type'>('draw');
  typedName = signal('');
  selectedFont = signal<SignatureFont>('Caveat');
  signatureValue = signal('');

  readonly fonts = FONTS.map((font) => ({label: font, value: font}));

  private drawCanvasRef = viewChild<ElementRef<HTMLCanvasElement>>('drawCanvas');
  private typeCanvasRef = viewChild<ElementRef<HTMLCanvasElement>>('typeCanvas');

  private isDrawing = false;
  private ctx: CanvasRenderingContext2D | null = null;

  ngAfterViewInit() {
    this.initDrawCanvas();
  }

  onResize() {
    if (this.activeTab() === 'draw') {
      this.initDrawCanvas();
    } else {
      this.renderTypedSignature();
    }
  }

  private initDrawCanvas() {
    const canvas = this.drawCanvasRef()?.nativeElement;
    if (!canvas) return;
    const width = canvas.offsetWidth;
    if (width <= 0) return;
    canvas.width = width;
    canvas.height = 160;
    this.ctx = canvas.getContext('2d');
    if (this.ctx) {
      this.ctx.lineCap = 'round';
      this.ctx.lineJoin = 'round';
      this.ctx.lineWidth = 2.5;
      this.ctx.strokeStyle = this.getStrokeColor();
    }
  }

  private getStrokeColor(): string {
    const fg = getComputedStyle(document.documentElement).getPropertyValue('--color-fg').trim();
    return fg || '#1f2937';
  }

  onPointerDown(event: PointerEvent) {
    const canvas = this.drawCanvasRef()?.nativeElement;
    if (!canvas || !this.ctx) return;
    this.isDrawing = true;
    canvas.setPointerCapture(event.pointerId);
    const {x, y} = this.getCoords(event, canvas);
    this.ctx.beginPath();
    this.ctx.moveTo(x, y);
  }

  onPointerMove(event: PointerEvent) {
    if (!this.isDrawing || !this.ctx) return;
    const canvas = this.drawCanvasRef()?.nativeElement;
    if (!canvas) return;
    const {x, y} = this.getCoords(event, canvas);
    this.ctx.lineTo(x, y);
    this.ctx.stroke();
  }

  onPointerUp() {
    if (!this.isDrawing) return;
    this.isDrawing = false;
    this.captureDrawSignature();
  }

  onPointerLeave() {
    if (!this.isDrawing) return;
    this.isDrawing = false;
    this.captureDrawSignature();
  }

  private captureDrawSignature() {
    const data = this.drawCanvasRef()?.nativeElement?.toDataURL('image/png');
    if (!data) return;
    this.signatureValue.set(data);
    this.signatureChange.emit(data);
  }

  private getCoords(event: PointerEvent, canvas: HTMLCanvasElement) {
    const rect = canvas.getBoundingClientRect();
    return {x: event.clientX - rect.left, y: event.clientY - rect.top};
  }

  clear() {
    const canvas = this.drawCanvasRef()?.nativeElement;
    if (canvas && this.ctx) {
      this.ctx.clearRect(0, 0, canvas.width, canvas.height);
    }
    this.typedName.set('');
    this.signatureValue.set('');
    this.signatureChange.emit('')
  }

  onTypedNameChange(name: string) {
    this.typedName.set(name);
    this.renderTypedSignature();
  }

  onFontChange(font: string) {
    this.selectedFont.set(font as SignatureFont);
    this.renderTypedSignature();
  }

  renderTypedSignature() {
    const name = this.typedName();
    if (!name.trim()) {
      return this.clear();
    }
    const canvas = this.typeCanvasRef()?.nativeElement;
    if (!canvas) return;
    const drawWidth = this.drawCanvasRef()?.nativeElement?.offsetWidth ?? 0;
    canvas.width = drawWidth > 0 ? drawWidth : 400;
    canvas.height = 160;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.font = `48px '${this.selectedFont()}', cursive`;
    ctx.fillStyle = this.getStrokeColor();
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(name, canvas.width / 2, canvas.height / 2);
    const data = canvas.toDataURL('image/png');
    this.signatureValue.set(data);
    this.signatureChange.emit(data);
  }
}
